/**
 * schemaGenerator.js
 * Central utility for generating JSON-LD Structured Data for BookMyAudition pages.
 * Follows schema.org and Google Search guidelines.
 */

const SITE_URL = "https://web.bookmyaudition.com";

/**
 * 1. Homepage Schema (Organization + WebSite + WebPage)
 */
export const getHomepageSchema = () => {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        "name": "BookMyAudition",
        "url": `${SITE_URL}/`,
        "logo": `${SITE_URL}/bma_logo_1.png`,
        "image": `${SITE_URL}/social_logo.jpg`,
        "description": "BookMyAudition is an online platform for discovering acting auditions and entertainment opportunities across India.",
        "sameAs": []
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        "url": `${SITE_URL}/`,
        "name": "BookMyAudition",
        "publisher": { "@id": `${SITE_URL}/#organization` },
        "inLanguage": "en-IN"
      },
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/#webpage`,
        "url": `${SITE_URL}/`,
        "name": "BookMyAudition - Acting Auditions in India",
        "description": "Discover acting auditions across India including Bollywood movies, web series, theatre and ad films.",
        "isPartOf": { "@id": `${SITE_URL}/#website` },
        "about": { "@id": `${SITE_URL}/#organization` },
        "inLanguage": "en-IN"
      }
    ]
  };
};

/**
 * 2. About Us Schema (AboutPage + Organization)
 */
export const getAboutUsSchema = () => {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": `${SITE_URL}/about/#webpage`,
        "url": `${SITE_URL}/about`,
        "name": "About BookMyAudition",
        "description": "Learn about BookMyAudition and its platform for discovering auditions and entertainment opportunities across India.",
        "about": { "@id": `${SITE_URL}/#organization` },
        "isPartOf": { "@id": `${SITE_URL}/#website` }
      },
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        "name": "BookMyAudition",
        "url": `${SITE_URL}/`,
        "description": "Online platform for discovering acting auditions and entertainment opportunities across India."
      }
    ]
  };
};

/**
 * 3. Audition Listing / Category Pages Schema (CollectionPage + ItemList + BreadcrumbList)
 */
export const getAuditionListingSchema = (items = [], categoryName = "Auditions & Casting Calls", categoryUrl = `${SITE_URL}/roles`) => {
  const itemListElements = items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "url": item.url || `${SITE_URL}/audition/${item.id || item.roleId || index + 1}/${item.urlSlug || 'audition'}`,
    "name": item.title || item.roleName || item.projectName || `Audition ${index + 1}`
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${categoryUrl}/#webpage`,
        "url": categoryUrl,
        "name": categoryName,
        "description": `Find acting, modeling, singing and dancing auditions across India. Browse ${categoryName} on BookMyAudition.`,
        "isPartOf": { "@id": `${SITE_URL}/#website` }
      },
      {
        "@type": "ItemList",
        "@id": `${categoryUrl}/#itemlist`,
        "name": `BookMyAudition ${categoryName}`,
        "itemListOrder": "https://schema.org/ItemListOrderAscending",
        "numberOfItems": items.length || 0,
        "itemListElement": itemListElements
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${categoryUrl}/#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": categoryName, "item": categoryUrl }
        ]
      }
    ]
  };
};

/**
 * 4. Individual Audition Schema (Event + BreadcrumbList)
 */
export const getIndividualAuditionSchema = (audition = {}) => {
  const auditionId = audition.id || audition._id || audition.roleId || '';
  const urlSlug = audition.urlSlug || 'audition-call';
  const auditionUrl = `${SITE_URL}/audition/${auditionId}/${urlSlug}`;
  const title = audition.projectName ? `${audition.projectName} - ${audition.roleName || 'Audition'}` : (audition.title || 'Acting Audition Call');
  const description = audition.description || audition.roleDescription || `Apply for ${audition.roleName || 'acting'} role in ${audition.projectName || 'project'}. Discover eligibility and casting details on BookMyAudition.`;
  const city = audition.city || audition.location || 'Mumbai';
  const state = audition.state || 'Maharashtra';
  const imageUrl = audition.bannerUrl || audition.imageUrl || `${SITE_URL}/social_logo.jpg`;

  const startDate = audition.createdAt || audition.startDate || new Date().toISOString();
  const endDate = audition.deadline || audition.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Event",
        "@id": `${auditionUrl}/#event`,
        "name": title,
        "description": description,
        "url": auditionUrl,
        "image": [imageUrl],
        "startDate": startDate,
        "endDate": endDate,
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": `${city} Audition Venue`,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": city,
            "addressRegion": state,
            "addressCountry": "IN"
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": "BookMyAudition",
          "url": `${SITE_URL}/`
        },
        "offers": {
          "@type": "Offer",
          "url": auditionUrl,
          "price": "0",
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${auditionUrl}/#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "Auditions", "item": `${SITE_URL}/roles` },
          { "@type": "ListItem", "position": 3, "name": title, "item": auditionUrl }
        ]
      }
    ]
  };
};

/**
 * 5. Person Profile Schema (Actor, Model, Singer, Dancer, Casting Director)
 */
export const getPersonProfileSchema = (profile = {}, type = "Actor") => {
  const profileId = profile.id || profile._id || '';
  const profileUrl = profile.url || `${SITE_URL}/profile/${profileId}`;
  const name = profile.name || profile.fullName || `${type} Profile`;
  const bio = profile.bio || profile.about || `${type} profile on BookMyAudition.`;
  const image = profile.imageUrl || profile.profilePic || `${SITE_URL}/social_logo.jpg`;
  const city = profile.city || "Mumbai";
  const state = profile.state || "Maharashtra";

  let jobTitle = "Actor";
  let knowsAbout = ["Acting", "Film", "Television", "Web Series", "Theatre"];

  if (type.toLowerCase().includes("model")) {
    jobTitle = "Model";
    knowsAbout = ["Fashion Modeling", "Commercial Modeling", "Print Modeling", "Runway Modeling"];
  } else if (type.toLowerCase().includes("singer")) {
    jobTitle = "Singer";
    knowsAbout = ["Singing", "Music", "Playback Singing", "Live Performance"];
  } else if (type.toLowerCase().includes("dancer")) {
    jobTitle = "Dancer";
    knowsAbout = ["Dance", "Choreography", "Live Performance", "Dance Performance"];
  } else if (type.toLowerCase().includes("casting")) {
    jobTitle = "Casting Director";
    knowsAbout = ["Casting", "Auditions", "Film Direction", "Talent Management"];
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${profileUrl}/#person`,
    "name": name,
    "url": profileUrl,
    "image": image,
    "description": bio,
    "jobTitle": jobTitle,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city,
      "addressRegion": state,
      "addressCountry": "IN"
    },
    "knowsAbout": knowsAbout
  };

  if (type.toLowerCase().includes("casting")) {
    schema.worksFor = {
      "@type": "Organization",
      "name": profile.companyName || "BookMyAudition",
      "url": `${SITE_URL}/`
    };
  }

  return schema;
};

/**
 * 6. FAQ Schema (FAQPage)
 */
export const getFAQSchema = (faqs = []) => {
  const defaultFaqs = [
    {
      question: "What is BookMyAudition?",
      answer: "BookMyAudition is an online platform for discovering acting auditions and entertainment opportunities across India."
    },
    {
      question: "What types of auditions can I find on BookMyAudition?",
      answer: "Users can discover relevant acting, modeling, singing, and dancing auditions listed on the BookMyAudition platform."
    },
    {
      question: "How can I find auditions on BookMyAudition?",
      answer: "Users can browse audition listings and review the requirements and application information for individual opportunities."
    },
    {
      question: "Are auditions on BookMyAudition free to apply?",
      answer: "Yes, performers can browse open audition calls and apply directly through the platform."
    }
  ];

  const faqList = faqs.length > 0 ? faqs : defaultFaqs;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_URL}/faq/#faq`,
    "mainEntity": faqList.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
};

/**
 * 7. Contact Page Schema (ContactPage)
 */
export const getContactSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${SITE_URL}/contact/#webpage`,
    "url": `${SITE_URL}/contact`,
    "name": "Contact BookMyAudition",
    "description": "Contact BookMyAudition for information and support."
  };
};

/**
 * 8. Breadcrumb List Schema
 */
export const getBreadcrumbSchema = (crumbs = []) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}/#breadcrumb`,
    "itemListElement": crumbs.map((crumb, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": crumb.name,
      "item": crumb.url.startsWith("http") ? crumb.url : `${SITE_URL}${crumb.url}`
    }))
  };
};
